import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { SurveyPDF } from 'survey-pdf';
import { getOrgTermsContent } from '../../../content/utils.js';

export const QUESTION_TYPES_DASHBOARD = [
  'radiogroup',
  'rating',
  'checkbox',
  'dropdown',
  'tagbox',
  'matrixdropdown',
  'matrix',
  'ranking',
];
export const CUSTOM_OPTIONS_SURVEY = {
  showLogicTab: false,
  showHeaderInEmptySurvey: true,
  allowChangeThemeInPreview: false,
  showThemeTab: false,
  showJSONEditorTab: false,
  showPreviewTab: false,
  showSidebar: false,
  defaultValue: false,
  questionTypes: [
    'radiogroup',
    'rating',
    'checkbox',
    'dropdown',
    'matrixdropdown',
    'matrix',
    'ranking',
    'text',
    'comment',
    'tagbox',
  ],
};

export const STATUSES_TEMPLATE = [
  {
    label: 'Draft',
    name: 'draft',
    description: getOrgTermsContent('surveyDraftStatusDesc'),
  },
  {
    label: 'Complete',
    name: 'complete',
    description: getOrgTermsContent('surveyCompleteStatusDesc'),
  },
  {
    label: 'Published',
    name: 'published',
    description: getOrgTermsContent('surveyPublishedStatusDesc'),
  },
];
export const CUSTOM_DEFAULT_FONT = 'Nunito, Helvetica, Arial, sans-serif';
export const CUSTOM_DEFAULT_BRAND_COLOR = '#27B0E0';

export const DEFAULT_COLOR_PALETTE = [
  '#4D4D4D',
  '#999999',
  '#FFFFFF',
  '#F44E3B',
  '#FE9200',
  '#FCDC00',
  '#DBDF00',
  '#A4DD00',
  '#68CCCA',
  '#73D8FF',
  '#AEA1FF',
  '#FDA1FF',
  '#333333',
  '#808080',
  '#cccccc',
  '#D33115',
  '#E27300',
  '#FCC400',
  '#B0BC00',
  '#68BC00',
  '#16A5A5',
  '#27B0E0',
  '#7B64FF',
  '#FA28FF',
  '#000000',
  '#666666',
  '#B3B3B3',
  '#9F0500',
  '#C45100',
  '#FB9E00',
  '#808900',
  '#194D33',
  '#0C797D',
  '#0062B1',
  '#653294',
  '#AB149E',
];

export const CUSTOM_FONTS = [
  {
    id: 'sq_352i_listHelvetica',
    name: 'Nunito, Helvetica, Arial, sans-serif',
  },
  {
    id: 'sq_352i_listOpen Sans',
    name: 'Open Sans',
  },
  {
    id: 'sq_352i_listArial, sans-serif',
    name: 'Arial, sans-serif',
  },
  {
    id: 'sq_352i_listCourier New, monospace',
    name: 'Courier New, monospace',
  },
  {
    id: 'sq_352i_listGaramond, serif',
    name: 'Garamond, serif',
  },
  {
    id: 'sq_352i_listGeorgia, serif',
    name: 'Georgia, serif',
  },
  {
    id: 'sq_352i_listTahoma, sans-serif',
    name: 'Tahoma, sans-serif',
  },
  {
    id: 'sq_352i_listTimes New Roman, serif',
    name: 'Times New Roman, serif',
  },
  {
    id: 'sq_352i_listTrebuchet MS, sans-serif',
    name: 'Trebuchet MS, sans-serif',
  },
  {
    id: 'sq_352i_listVerdana, sans-serif',
    name: 'Verdana, sans-serif',
  },
];

export const formatedTemplate = (input) => {
  // Handle case where surveyJson is passed directly (string or object)
  if (typeof input === 'string') {
    try {
      return JSON.parse(input);
    } catch (e) {
      return null;
    }
  }

  if (typeof input === 'object' && input !== null) {
    // If input has surveyJson property, extract it
    if ('surveyJson' in input) {
      const { surveyJson } = input;

      if (typeof surveyJson === 'string') {
        try {
          return JSON.parse(surveyJson);
        } catch (e) {
          return null;
        }
      }

      if (typeof surveyJson === 'object' && surveyJson !== null) {
        return surveyJson;
      }

      return null;
    }

    // Otherwise assume input is already the template object
    return input;
  }

  return null;
};

export const applyBrandSettings = (settings) => {
  if (settings) {
    if (settings.fontName) {
      const style = document.createElement('style');
      style.innerHTML = `:root { --sjs-font-family: ${settings.fontName}; }`;
      document.head.appendChild(style);
    }
    if (settings.brandColor) {
      const style = document.createElement('style');
      style.innerHTML = `:root { --primary: ${settings.brandColor}; --secondary: ${settings.brandColor}; --sjs-primary-backcolor-dark: color-mix(in srgb, ${settings.brandColor} 45%, white);  --primary-light: color-mix(in srgb, ${settings.brandColor} 45%, white); }`;
      document.head.appendChild(style);
    }
  }
};

export const generateSurveyPDF = (template) => {
  const { title, surveyJson } = template;

  const json = formatedTemplate(surveyJson) || surveyJson;
  const modifiedSurveyJson = {
    ...json,
    showQuestionNumbers: 'off',
  };

  const options = {
    haveCommercialLicense: true,
    fontSize: 14,
    margins: {
      left: 10,
      right: 10,
      top: 10,
      bot: 10,
    },
  };

  const surveyPDF = new SurveyPDF(modifiedSurveyJson, options);

  surveyPDF.save(`${title || 'Survey_Template'}.pdf`);
};

const generateTableData = (surveyJson, surveyResponses, tableHeaders) => {
  return surveyResponses.map((response) => {
    const surveyResult =
      typeof response.surveyResult === 'string'
        ? JSON.parse(response.surveyResult)
        : response.surveyResult;

    return tableHeaders.map((questionTitle) => {
      const question = surveyJson.pages
        .reduce((acc, page) => acc.concat(page.elements || []), [])
        .filter(Boolean)
        .find((q) => q.title === questionTitle);

      if (!question) return '';

      const answer = surveyResult[question.name];
      const commentKey = `${question.name}-Comment`;
      const comment = surveyResult[commentKey]
        ? ` - Comment: ${surveyResult[commentKey]}`
        : '';

      if (!answer) return '';

      if (
        question.type === 'matrix' &&
        typeof answer === 'object' &&
        !Array.isArray(answer)
      ) {
        return (
          Object.entries(answer)
            .map(([rowKey, colKey]) => {
              const row = question.rows.find((r) => r.value === rowKey);
              const rowText = row ? row.text : rowKey;

              const column = question.columns.find((c) => c.value === colKey);
              const colTitle = column ? column.text : colKey;

              return `${rowText}: ${colTitle}`;
            })
            .join(' | ') + comment
        );
      }

      if (
        question.type === 'matrixdropdown' &&
        typeof answer === 'object' &&
        !Array.isArray(answer)
      ) {
        return (
          Object.entries(answer)
            .map(([rowKey, columns]) => {
              const row = question.rows.find((r) => r.value === rowKey);
              const rowText = row && row.text ? row.text : rowKey;

              return `${rowText}: ${Object.entries(columns)
                .map(([colKey, value]) => {
                  const column = question.columns.find(
                    (c) => c.name === colKey
                  );
                  const colTitle =
                    column && column.title ? column.title : colKey;

                  return `${colTitle} = ${value}`;
                })
                .join(', ')}`;
            })
            .join(' | ') + comment
        );
      }

      // PREV
      // if (question.type === 'matrixdropdown' && typeof answer === 'object' && !Array.isArray(answer)) {
      //   return (
      //     Object.entries(answer)
      //       .map(([rowKey, columns]) => {
      //         const row = question.rows.find((r) => r.value === rowKey);
      //         const rowText = row ? row.text : rowKey;
      //
      //         return `${rowText}: ${Object.entries(columns)
      //           .map(([colKey, value]) => {
      //             const column = question.columns.find((c) => c.name === colKey);
      //             const colTitle = column ? column.title : colKey;
      //
      //             return `${colTitle} = ${value}`;
      //           })
      //           .join(', ')}`;
      //       })
      //       .join(' | ') + comment
      //   );
      // }

      if (Array.isArray(answer)) {
        return (
          answer
            .map((item) => {
              const choice =
                question.choices &&
                question.choices.find((c) => c.value === item);
              return choice ? choice.text : item;
            })
            .join(', ') + comment
        );
      }

      const choice =
        question.choices && question.choices.find((c) => c.value === answer);
      return (choice ? choice.text : answer || '') + comment;
    });
  });
};

function generateSurveyPDFContent(doc, surveyJson, surveyResponses) {
  doc.setFontSize(16);
  doc.setTextColor(21, 49, 66);

  const tableHeaders = surveyJson.pages
    .reduce((acc, page) => {
      return acc.concat(page.elements || []);
    }, [])
    .filter(Boolean)
    .map((question) => {
      return question.title;
    });

  const tableData = generateTableData(
    surveyJson,
    surveyResponses,
    tableHeaders
  );

  const columnCount = tableHeaders.length;
  const columnWidth = 60;
  const dynamicPageWidth = columnWidth * columnCount;

  doc.internal.pageSize.height = 197;
  doc.text(surveyJson.title || 'Survey Report', 5, 12);
  let isFirstPage = true;

  doc.autoTable({
    head: [tableHeaders],
    body: tableData,
    startY: 20,
    startX: 10,
    styles: {
      fontSize: 10,
      cellPadding: 3,
      textColor: [21, 49, 66],
      fontStyle: 'normal',
      lineWidth: 0,
    },
    headStyles: { fillColor: [247, 250, 253], textColor: [21, 49, 66] },
    theme: 'plain',
    willDrawCell(data) {
      if (data.section === 'body' && data.row.index % 2 === 1) {
        doc.setFillColor(247, 250, 253);
        doc.rect(
          data.cell.x,
          data.cell.y,
          data.cell.width,
          data.cell.height,
          'F'
        );
      }
    },
    tableWidth: dynamicPageWidth - 10,
    didDrawPage() {
      if (isFirstPage) {
        isFirstPage = false;
      }
      doc.internal.pageSize.width = dynamicPageWidth;
      doc.setFontSize(16);
    },
    margin: {
      top: 5,
      left: 5,
      right: 0,
      bottom: 0,
    },
  });
}

function generateSurveyExcel(surveyJson, surveyResponses, title) {
  const tableHeaders = surveyJson.pages
    .reduce((acc, page) => {
      return acc.concat(page.elements || []);
    }, [])
    .filter(Boolean)
    .map((question) => {
      return question.title;
    });

  const tableData = generateTableData(
    surveyJson,
    surveyResponses,
    tableHeaders
  );

  const worksheet = XLSX.utils.aoa_to_sheet([tableHeaders].concat(tableData));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Survey Responses');
  XLSX.writeFile(workbook, `${title}.xlsx`);
}

function generateSurveyCSV(surveyJson, surveyResponses, title) {
  const tableHeaders = surveyJson.pages
    .reduce((acc, page) => {
      return acc.concat(page.elements || []);
    }, [])
    .filter(Boolean)
    .map((question) => {
      return question.title;
    });

  const tableData = generateTableData(
    surveyJson,
    surveyResponses,
    tableHeaders
  );

  const csvContent = [tableHeaders]
    .concat(tableData)
    .map((row) => {
      return row.join(',');
    })
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', `${title}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function generateSurveyResponse(json, surveyResponses, format, title) {
  const surveyJson = typeof json === 'string' ? JSON.parse(json) : json;
  if (format === 'pdf') {
    const doc = new jsPDF();
    generateSurveyPDFContent(doc, surveyJson, surveyResponses);
    doc.save(`${title}.pdf`);
  } else if (format === 'xlsx') {
    generateSurveyExcel(surveyJson, surveyResponses, title);
  } else if (format === 'csv') {
    generateSurveyCSV(surveyJson, surveyResponses, title);
  } else {
    console.error("Invalid format specified. Use 'pdf', 'xlsx', or 'csv'.");
  }
}
